import type { ReactNode } from "react";
import { ArrowRight, FileText, Plus, Quote } from "lucide-react";
import { Avatar, cx } from "@amber/ui";
import type { SearchResponse } from "@amber/application/contracts";
import {
  LIFECYCLE_LABELS,
  Snippet,
  itemMeta,
  itemStatus,
  passageMeta,
  statusTone,
  timeLabel,
} from "../../views/search/hits";

export interface Option {
  id: string;
  href: string | null;
  disabled?: string;
  content: ReactNode;
}

export function OptionRow({
  id,
  active,
  disabled,
  onPick,
  onHover,
  className,
  children,
}: {
  id: string;
  active: boolean;
  disabled?: string | undefined;
  onPick: () => void;
  onHover: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      aria-disabled={disabled ? true : undefined}
      title={disabled}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={onPick}
      onMouseMove={onHover}
      className={cx(
        "mx-1 flex min-w-0 cursor-pointer items-start gap-2.5 rounded-[7px] px-2 py-1.5 hover:bg-surface",
        active && "bg-row-selected shadow-[inset_2px_0_0_var(--color-link)] hover:bg-row-selected",
        disabled && "cursor-not-allowed text-muted",
        className,
      )}
    >
      {children}
    </div>
  );
}

function OptionIcon({ children, round, accent }: { children: ReactNode; round?: boolean; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "mt-px grid size-[22px] flex-none place-items-center font-mono text-[8.5px] font-semibold",
        round ? "rounded-full bg-surface-3" : "rounded-md bg-surface-2 text-muted",
        accent && "bg-accent-soft text-link",
      )}
    >
      {children}
    </span>
  );
}

export function GroupHeading({ id, label, count }: { id: string; label: string; count?: number }) {
  return (
    <div
      id={id}
      className="flex items-baseline gap-2 px-3 pt-2.5 pb-1 text-xs font-semibold tracking-[0.04em] text-muted uppercase"
    >
      {label}
      {count === undefined ? null : <span className="font-normal tracking-normal normal-case">{count}</span>}
    </div>
  );
}

export interface OptionGroup {
  key: string;
  label: string;
  count?: number;
  options: Option[];
  note?: ReactNode;
}

const link = (id: string, href: string, label: string): Option => ({
  id,
  href,
  content: (
    <span className="flex items-center gap-1.5 pl-8 text-md font-strong text-link">
      <ArrowRight size={12} aria-hidden="true" />
      {label}
    </span>
  ),
});

const all = (key: string, total: number, href: string, where?: string) =>
  link(`all-${key}`, href, `See all ${String(total)}${where ? ` in ${where}` : ""}`);

export function buildOptions(data: SearchResponse | undefined, q: string): { groups: OptionGroup[] } {
  if (!data) return { groups: [] };
  const qs = encodeURIComponent(q.trim());
  if (data.url) return urlGroups(data, data.url, q);
  return resultGroups(data, qs);
}

function urlGroups(data: SearchResponse, u: NonNullable<SearchResponse["url"]>, q: string): { groups: OptionGroup[] } {
  const options: Option[] = [];
  if (u.outcome === "already_collected") {
    if (u.item) {
      const item = u.item;
      options.push({
        id: `url-item-${item.id}`,
        href: `/backlog/${item.id}`,
        content: (
          <>
            <OptionIcon>
              <FileText size={13} />
            </OptionIcon>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-strong">Already collected: {item.title}</span>
              <span className="block truncate text-sm text-muted">{u.platform?.label}</span>
            </span>
            <span className="mt-0.5 flex-none text-sm text-muted">{item.status}</span>
          </>
        ),
      });
      options.push(link("url-record", `/items/${item.id}`, "Open the evidence record"));
    }
    return {
      groups: [
        {
          key: "url",
          label: "Already collected",
          options,
          note: u.item ? undefined : <None title="Already collected">{u.message}</None>,
        },
      ],
    };
  }
  if (u.outcome === "new") {
    const canAdd = data.can_add.enabled;
    options.push({
      id: "url-add",
      href: canAdd ? `/backlog?add=${encodeURIComponent(q.trim())}` : null,
      ...(data.can_add.enabled ? {} : { disabled: data.can_add.reason }),
      content: (
        <>
          <OptionIcon accent>
            <Plus size={13} />
          </OptionIcon>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-strong">Add this URL…</span>
            <span className="block truncate text-sm text-muted">
              {canAdd
                ? "Opens Add item in the Backlog with this URL filled in; it shows as New"
                : "Only Editors and above can add items"}
            </span>
          </span>
        </>
      ),
    });
    return {
      groups: [
        {
          key: "url",
          label: "Not in the workspace",
          options,
          note: (
            <None title="Not collected yet">{`No item has this address. It is recognized as ${u.platform?.label ?? "a collected platform"}.`}</None>
          ),
        },
      ],
    };
  }
  if (u.outcome === "known_account" && u.source) {
    options.push(link("url-source", `/sources/${u.source.id}`, `Known account: ${u.source.title}`));
    return {
      groups: [
        {
          key: "url",
          label: "Known account",
          options,
          note: (
            <None
              title={u.message}
            >{`${u.platform?.label ?? "This platform"} isn't collected yet, so items can't be added from it.`}</None>
          ),
        },
      ],
    };
  }
  if (u.outcome === "not_collected") {
    return {
      groups: [
        {
          key: "url",
          label: "Not collected",
          options: [],
          note: (
            <None
              title={u.message}
            >{`Items can't be added from ${u.platform?.label ?? "this platform"} yet. Known accounts on it stay recorded on each profile.`}</None>
          ),
        },
      ],
    };
  }
  return {
    groups: [
      {
        key: "url",
        label: u.outcome === "source" ? "Source address" : "Not a usable link",
        options: [],
        note: (
          <None title={u.outcome === "source" ? "Not a single item" : "This link can't be checked"}>{u.message}</None>
        ),
      },
    ],
  };
}

function resultGroups(data: SearchResponse, qs: string): { groups: OptionGroup[] } {
  const groups: { key: string; label: string; count?: number; options: Option[] }[] = [];
  if (data.items.total > 0) {
    groups.push({
      key: "items",
      label: "Items",
      count: data.items.total,
      options: [
        ...data.items.rows.map((item): Option => ({
          id: `item-${item.id}`,
          href: `/backlog/${item.id}`,
          content: (
            <>
              <OptionIcon>
                <FileText size={13} />
              </OptionIcon>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-strong">{item.title}</span>
                <span className="block truncate text-sm text-muted">{itemMeta(item)}</span>
              </span>
              <span className={cx("mt-0.5 flex-none text-sm whitespace-nowrap", statusTone(item.status))}>
                {itemStatus(item)}
              </span>
            </>
          ),
        })),
        ...(data.items.total > data.items.rows.length
          ? [all("items", data.items.total, `/backlog?q=${qs}`, "Backlog")]
          : []),
      ],
    });
  }
  if (data.passages.total > 0) {
    groups.push({
      key: "passages",
      label: "Transcripts",
      count: data.passages.total,
      options: [
        ...data.passages.rows.map((p): Option => ({
          id: `passage-${p.unit_id}`,
          href: p.href,
          content: (
            <>
              <OptionIcon>
                <Quote size={13} />
              </OptionIcon>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-strong">
                  {p.speaker} <span className="font-normal text-muted">· {timeLabel(p.start_ms) ?? "text"}</span>
                </span>
                <span className="line-clamp-2 text-md text-muted">
                  <Snippet segments={p.snippet} />
                </span>
                <span className="block truncate text-sm text-muted">{passageMeta(p)}</span>
              </span>
              {p.readiness ? (
                <span
                  className={cx(
                    "mt-0.5 flex-none text-sm whitespace-nowrap",
                    p.readiness.result === "eligible" ? "text-fg" : "text-muted",
                  )}
                >
                  {p.readiness.label}
                </span>
              ) : null}
            </>
          ),
        })),
        ...(data.passages.total > data.passages.rows.length
          ? [all("passages", data.passages.total, `/search?q=${qs}`)]
          : []),
      ],
    });
  }
  if (data.people.total > 0) {
    groups.push({
      key: "people",
      label: "People",
      count: data.people.total,
      options: [
        ...data.people.rows.map((p): Option => ({
          id: `person-${p.id}`,
          href: `/backlog?person=${p.id}`,
          content: (
            <>
              <Avatar name={p.name} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-strong">{p.name}</span>
                <span className="block truncate text-sm text-muted">
                  {p.alias ? `Also known as ${p.alias}` : "Opens their items in the Backlog"}
                </span>
              </span>
              <span className="mt-0.5 flex-none text-sm text-muted">{LIFECYCLE_LABELS[p.lifecycle_state]}</span>
            </>
          ),
        })),
        ...(data.people.total > data.people.rows.length ? [all("people", data.people.total, `/search?q=${qs}`)] : []),
      ],
    });
  }
  return { groups };
}

export function None({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-3 pt-3 pb-2 text-md text-muted">
      <b className="mb-0.5 block font-semibold text-fg">{title}</b>
      {children}
    </div>
  );
}
